import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl, NgModel } from '@angular/forms';
import { SvcControl } from '../svc-control';
import { Subject, takeUntil, tap } from 'rxjs';
import { cloneDeep } from 'lodash';
import { MatChipGrid } from '@angular/material/chips';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatDialogRef } from '@angular/material/dialog';
import { AgVirtualSrollComponent } from 'ag-virtual-scroll';
import _ from 'lodash';
import { SvcMediaQuery, SvcTestIdDirective } from 'projects/lib-shared-common/src/public-api';
import { MAX_OPTIONS_LIST_HEIGHT } from '../utils/droplists.const';
import { optionsContainer } from '../utils/droplists.animations';
import { ISvcSelectOption } from './interfaces/svc-select.interface';

@Component({
  selector: 'svc-select',
  templateUrl: './svc-select.component.html',
  styleUrls: ['./svc-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SvcSelectComponent),
      multi: true,
    },
  ],
  animations: [optionsContainer],
})
export class SvcSelectComponent extends SvcControl implements OnInit, OnChanges {
  @HostBinding('class.svc-field-in-primary') protected inPrimaryAppearance = false;

  private _options: ISvcSelectOption[] = [];
  private _lastFilteredOptions: ISvcSelectOption[] = [];
  private _collapsedGroups = new Set<string>();
  private readonly _mobileOverlayMargin = 16;

  public canShowSearchInput: boolean;
  public maxOptionsListHeight = MAX_OPTIONS_LIST_HEIGHT;

  @Input() label: string | { text: string; icon: string };
  @Input() placeholder: string;
  @Input() set options(value: ISvcSelectOption[]) {
    const options = [...(value ? value : [])];
    this._options = _.cloneDeep(options);
    this._collapsedGroups.clear();
    this._checkCanShowSearchInput();
  }
  @Input() appearance: 'default' | 'primary' = 'default';
  @Input() icon: string;
  @Input() readonly: boolean = false;
  /** Additive, defaults to current behavior (generic validation message shown). Set false when the
   *  consumer renders its own field-specific error instead — see RCA Type Registration. */
  @Input() showValidation: boolean = true;
  /** Additive, defaults to current behavior (no marker shown). Set true to render a red "*" after
   *  the label — see RCA Type Registration. */
  @Input() required: boolean = false;
  /** Additive, defaults to current behavior (marker follows `required`). Set false to keep the
   *  required validation but hide the red "*" after the label — see CIL Plan Setup. */
  @Input() showRequiredMarker: boolean = true;
  @Input() readonlyStyle: 'default' | 'chip' | 'transparent-chip' = 'default';
  @Input() truncateTextValue: boolean = false;
  @Input() inputId: string;
  @Input() fallbackTextValue: string;
  @Input() loading: boolean = false;
  @Input() externalSearch: boolean;
  @Input() minCharactersExternalSearchLength = 3;
  @Input() hideOptionsWhenSelected = true;
  /**
   * AB#72327 — when true, the mobile options overlay (below MD, where the list is already detached
   * from the field and centered on the viewport) spans the full viewport width instead of keeping
   * the default 16px side margin. Opt-in, so every other screen keeps the margin it has today; set
   * it where the container itself goes edge to edge on phone (CIL "Envio para aprovação").
   *
   * `booleanAttribute` so the bare-attribute form (`<svc-select mobileOverlayFullWidth>`) coerces
   * like any other boolean input. The surrounding legacy inputs are left as-is on purpose — only
   * new API follows `component-api-design`.
   */
  @Input({ transform: booleanAttribute }) mobileOverlayFullWidth = false;

  @Output() ngModelChange = new EventEmitter<any>();
  @Output() searchTextChange = new EventEmitter<string>();

  @Optional() @ContentChild(TemplateRef) templateRef: TemplateRef<any>;
  @ViewChild('formField', { read: ElementRef }) formField: ElementRef<HTMLElement>;
  @ViewChild('optionsListTemplate') optionsListTemplate: TemplateRef<any>;
  @ViewChild(MatChipGrid) matChipGrid: MatChipGrid;
  @ViewChild(AgVirtualSrollComponent) agVirtualScroll: AgVirtualSrollComponent;

  public filteredOptions: ISvcSelectOption[] = [];
  public selected: ISvcSelectOption;
  protected searchText: string = null;
  protected optionsListIsOpened = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _isUpdatingControl = false;
  private _overlayRef: OverlayRef;
  private _initialDisableClose = false;
  private _overlayScrollCleanup?: () => void;
  private _scrollCdScheduled = false;

  private get _isTemplateDrivenNgModel(): boolean {
    try {
      const ngControl = this.injector?.get(NgControl, null as any);
      return ngControl instanceof NgModel;
    } catch {
      return false;
    }
  }

  constructor(
    @Inject(Injector) injector: Injector,
    @Optional() private dialogRef: MatDialogRef<any>,
    private _viewContainerRef: ViewContainerRef,
    private _overlay: Overlay,
    private _mediaQuery: SvcMediaQuery,
    private cdr: ChangeDetectorRef,
    private _ngZone: NgZone,
    @Optional() @Self() private readonly _svcTestId?: SvcTestIdDirective
  ) {
    super(injector);
    if (this._svcTestId) this._svcTestId.useOnHost = false;
  }

  public get inputTestId(): string | null {
    return this._svcTestId?.normalizedTestId || null;
  }

  private _setupOverlayScrollFix(): void {
    this._overlayScrollCleanup?.();

    const overlayRoot = this._overlayRef?.overlayElement;
    if (!overlayRoot) return;

    const targets = new Set<HTMLElement>();
    const agVirtualScrollEl = overlayRoot.querySelector('ag-virtual-scroll') as HTMLElement | null;
    if (agVirtualScrollEl) targets.add(agVirtualScrollEl);

    const overflowAutoEl = overlayRoot.querySelector('.overflow-y-auto') as HTMLElement | null;
    if (overflowAutoEl) targets.add(overflowAutoEl);

    if (!targets.size) targets.add(overlayRoot);

    const onScroll = () => {
      if (this._scrollCdScheduled) return;
      this._scrollCdScheduled = true;
      requestAnimationFrame(() => {
        this._scrollCdScheduled = false;
        if (!this.optionsListIsOpened) return;
        try {
          this._ngZone.run(() => {
            this.agVirtualScroll?.refreshData?.();
            this.cdr.detectChanges();
          });
        } catch {}
      });
    };

    const cleanups: Array<() => void> = [];
    targets.forEach((el) => {
      el.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => el.removeEventListener('scroll', onScroll));
    });

    this._overlayScrollCleanup = () => {
      cleanups.forEach((fn) => fn());
      this._scrollCdScheduled = false;
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this._initialDisableClose = this.dialogRef?.disableClose ?? false;

    this._mediaQuery.size$
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap(() => this._checkCanShowSearchInput())
      )
      .subscribe();

    this.control?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap((value: any) => {
          if (!this._isUpdatingControl) {
            if (!this.loading) {
              this.selected = this._findOptionByValue(value);
            }
            this._options = cloneDeep(this._options ?? []);
            this.searchText = '';
            this.filterOptions(this.searchText);
          }
        })
      )
      .subscribe();
    this._checkOptions();
    this._checkSelected();
    this.filterOptions(null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._checkOptions();
    this._checkSelected();
    this._update();
    this.filterOptions(this.searchText);
    this._checkCanShowSearchInput();
    if ('appearance' in changes) {
      this.inPrimaryAppearance = this.appearance === 'primary';
    }
  }

  private _checkCanShowSearchInput() {
    const size = this._mediaQuery.currentSize;
    const hideBecauseSelected = this.hideOptionsWhenSelected && !!this.selected;
    this.canShowSearchInput = !hideBecauseSelected && ((this._options?.length > 100 && size.isXS) || size.isAboveXS);
  }

  private _checkOptions(): void {
    this._options = (this._options ?? []).map((option) => {
      return (['string', 'number', 'boolean'].includes(typeof option) ? { text: option, value: option } : option) as ISvcSelectOption;
    });
  }

  private _checkSelected(): void {
    if (this.loading) return;
    this.selected = this._findOptionByValue(this.control?.value);
  }

  public filterOptionsByApi(): void {
    if (this.externalSearch) this.searchTextChange.emit(this.searchText);
  }

  public filterOptions(value: string): void {
    const shouldExcludeSelected = this.hideOptionsWhenSelected && !!this.selected;
    if (this.externalSearch) {
      const options = shouldExcludeSelected ? this._options?.filter((option) => option.value !== this.selected?.value) : this._options;
      this._setFilteredOptions(options ?? []);
      return;
    }
    const filterValue = value ? value.toString().toLowerCase() : '';
    const filtered = (this._options ?? []).filter((option) => {
      const matchesSearch = !filterValue || option.text.toString().toLowerCase().includes(filterValue);
      if (!matchesSearch) return false;
      if (!shouldExcludeSelected) return true;
      return option.value !== this.selected?.value;
    });
    this._setFilteredOptions(filtered);
  }

  private _available(value: any) {
    const item = this._findOptionByValue(value);
    if (item) {
      this._options = cloneDeep(this._options ?? []);
      this._update();
    }
  }

  private _unavailable(value: any) {
    const item = this._findOptionByValue(value);
    if (item) {
      this.selected = item;
      this._options = cloneDeep(this._options ?? []);
      this._update();
    }
  }

  private _findOptionByValue(value: any): ISvcSelectOption | null {
    if (value === null || value === undefined) return null;

    return (this._options ?? []).find((option) => this._valuesMatch(option?.value, value)) ?? null;
  }

  private _valuesMatch(a: any, b: any): boolean {
    if (a === null || a === undefined || b === null || b === undefined) return false;

    if (typeof a === 'object' || typeof b === 'object') {
      return a === b;
    }

    if (typeof a === typeof b) {
      return a === b;
    }

    return a.toString() === b.toString();
  }

  private _update() {
    if (this.loading) return;
    if (this.selected?.value != this.control?.value) {
      try {
        this._isUpdatingControl = true;
        this.control?.setValue(this.selected?.value);
      } finally {
        this._isUpdatingControl = false;
      }

      // When used with reactive forms (`formControlName`), `ngModelChange` is not emitted automatically.
      // Emit it here so consumers can rely on `(ngModelChange)` consistently.
      if (!this._isTemplateDrivenNgModel) {
        this.ngModelChange.emit(this.selected?.value);
      }
    }
  }

  openOptions() {
    if (this.loading || this.optionsListIsOpened || (this.control?.disabled ?? false)) {
      return;
    }

    const hasOverlayRefInstance = !!this._overlayRef;

    if (!this._overlayRef && this.formField.nativeElement) {
      this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: 'bg-transparent',
        scrollStrategy: this._overlay.scrollStrategies.block(),
        width: this._getOverlayWidth(),
        maxHeight: MAX_OPTIONS_LIST_HEIGHT,
        positionStrategy: this._getOptionsPositionStrategy(),
      });

      this._overlayRef
        .backdropClick()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.closeOptions();
        });

      this._overlayRef
        .detachments()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.formField?.nativeElement?.focus();
          if (this.dialogRef) this.dialogRef.disableClose = this._initialDisableClose;
          setTimeout(() => {
            this.optionsListIsOpened = false;
            this.cdr.markForCheck();
          });
        });
    }

    if (hasOverlayRefInstance) {
      this._overlayRef?.updateSize({
        width: this._getOverlayWidth(),
      });
      this._overlayRef.updatePositionStrategy(this._getOptionsPositionStrategy());
    }

    if (!this.optionsListIsOpened) {
      try {
        const viewRef = this._overlayRef.attach(new TemplatePortal(this.optionsListTemplate, this._viewContainerRef));
        this.optionsListIsOpened = true;
        this._setupOverlayScrollFix();
        if (this.dialogRef) this.dialogRef.disableClose = true;

        setTimeout(() => {
          if (this.optionsListIsOpened) {
            const tmp = [...this.filteredOptions];
            this.filteredOptions = [];
            this.cdr.detectChanges();
            this.filteredOptions = tmp;

            const element = viewRef.rootNodes[0] as HTMLElement;
            if (element?.querySelector) {
              const input: HTMLInputElement = element.querySelector('#app-svc-select-search-input-options');
              if (input) {
                input?.focus();
              } else {
                this.formField?.nativeElement?.focus();
              }
            }
          }
        });
      } catch {}
    }
  }

  closeOptions(options?: { fromOnDestroy?: boolean }) {
    const isOpened = this._overlayRef?.hasAttached() ?? false;
    this._overlayRef?.detach();
    this._overlayScrollCleanup?.();
    isOpened && !(options?.fromOnDestroy ?? false) && this.control?.markAsTouched();
  }

  private _getOptionsPositionStrategy() {
    const fieldWrapper = this.formField.nativeElement.querySelector('.mat-mdc-text-field-wrapper') as HTMLElement;
    const isMobile = this._mediaQuery.currentSize?.isBelowMD ?? false;
    if (isMobile) {
      const rect = this.formField.nativeElement.getBoundingClientRect();
      const offsetY = fieldWrapper ? this.formField.nativeElement.clientHeight - fieldWrapper.offsetHeight : 0;
      return this._overlay
        .position()
        .global()
        .centerHorizontally()
        .top(`${Math.max(0, rect.bottom + offsetY)}px`);
    }

    const strategy = this._overlay
      .position()
      .flexibleConnectedTo(this.formField.nativeElement)
      .withLockedPosition(true)
      .withPush(true)
      .withViewportMargin(0)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: fieldWrapper ? this.formField.nativeElement.clientHeight - fieldWrapper.offsetHeight : 0,
        },
      ]);
    return strategy;
  }

  private _getOverlayWidth(): number {
    const fieldWidth = this.formField?.nativeElement?.clientWidth ?? 0;
    const viewportWidth = document.body?.parentElement?.clientWidth ?? window.innerWidth ?? fieldWidth;
    const isMobile = this._mediaQuery.currentSize?.isBelowMD ?? false;
    if (!isMobile) return fieldWidth;
    const margin = this.mobileOverlayFullWidth ? 0 : this._mobileOverlayMargin;
    const desired = Math.max(0, viewportWidth - margin * 2);
    return Math.max(fieldWidth, desired);
  }

  remove(): void {
    const prevValue = this.selected.value;
    this.selected = null;
    this._available(prevValue);
    this.filterOptions(this.searchText);
  }

  select(option: ISvcSelectOption): void {
    if (option?.isGroupLabel || option?.disabled) {
      return;
    }
    this._unavailable(option.value);
    this.closeOptions();
    this.filterOptions(this.searchText);
  }

  toggleGroup(groupKey: string, event?: Event): void {
    if (!groupKey) return;
    event?.preventDefault();
    event?.stopPropagation();
    if (this._collapsedGroups.has(groupKey)) {
      this._collapsedGroups.delete(groupKey);
    } else {
      this._collapsedGroups.add(groupKey);
    }
    this._refreshFilteredOptions();
  }

  onOptionClick(option: ISvcSelectOption): void {
    this.select(option);
  }

  onOptionKeySelect(event: KeyboardEvent, option: ISvcSelectOption): void {
    if (option?.isGroupLabel || option?.disabled) {
      return;
    }
    event.preventDefault();
    this.select(option);
  }

  forceFocusItemWhenDownPressed(event: Event) {
    event.preventDefault();
    const hideBecauseSelected = this.hideOptionsWhenSelected && !!this.selected;
    if (this.filteredOptions.length > 0 && !hideBecauseSelected) {
      const container = (event.target as HTMLElement).parentElement.parentElement.children[1];
      if (container?.children.length > 0) {
        const item = container?.querySelector('.select-option') as HTMLElement;
        item?.focus();
      }
    }
  }

  @HostListener('window:resize')
  protected onWindowResize() {
    if (this.optionsListIsOpened) {
      this._overlayRef?.updateSize({
        width: this._getOverlayWidth(),
      });
      this._overlayRef?.updatePositionStrategy(this._getOptionsPositionStrategy());
    }
  }

  @HostListener('document:keydown.esc', ['$event'])
  protected onEsc(event: Event) {
    if (this.optionsListIsOpened) {
      this.closeOptions();
    }
  }

  public ngOnDestroy(): void {
    this.closeOptions({ fromOnDestroy: true });
    this._overlayScrollCleanup?.();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  protected onValueChanged(value): void {
    this.ngModelChange.emit(value);
  }

  focusSibling(event: Event, direction: 'next' | 'previous'): void {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (!target) return;
    let sibling = direction === 'next' ? target.nextElementSibling : target.previousElementSibling;
    while (sibling && sibling.classList?.contains('select-group-label')) {
      sibling = direction === 'next' ? sibling.nextElementSibling : sibling.previousElementSibling;
    }
    (sibling as HTMLElement)?.focus();
  }

  isGroupCollapsed(groupKey?: string | null): boolean {
    if (!groupKey) return false;
    return this._collapsedGroups.has(groupKey);
  }

  private _withGroupLabels(options: ISvcSelectOption[]): ISvcSelectOption[] {
    if (!options?.length) return [];
    const result: ISvcSelectOption[] = [];
    let lastGroupKey: string = null;
    options.forEach((option, index) => {
      const groupKey = option.groupKey ?? null;
      const hasGroup = !!groupKey;
      if (hasGroup && groupKey !== lastGroupKey) {
        lastGroupKey = groupKey;
        result.push({
          text: option.groupLabel ?? option.groupKey,
          value: `__svc-select-group-${groupKey}-${index}`,
          isGroupLabel: true,
          groupKey,
        });
      } else if (!hasGroup) {
        lastGroupKey = null;
      }
      result.push(option);
    });
    return result;
  }

  private _setFilteredOptions(baseOptions: ISvcSelectOption[]): void {
    this._lastFilteredOptions = baseOptions ?? [];
    this._refreshFilteredOptions();
  }

  private _refreshFilteredOptions(): void {
    const withGroups = this._withGroupLabels(this._lastFilteredOptions ?? []);
    this.filteredOptions = this._applyGroupCollapse(withGroups);
  }

  private _applyGroupCollapse(options: ISvcSelectOption[]): ISvcSelectOption[] {
    if (!options?.length) return [];
    return options.filter((option) => option.isGroupLabel || !this.isGroupCollapsed(option.groupKey));
  }
}
